import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { BoardLedLine } from '../../../types/device';

interface BoardLedLineListProps {
  lines: BoardLedLine[];
  /** The holds each line took, in strip order. */
  ledHolds: number[][];
  /** Where each line's LEDs start, the derived ones included. */
  ledStarts: number[];
  selected: number | null;
  onSelect: (index: number | null) => void;
  onChange: (lines: BoardLedLine[]) => void;
}

/**
 * The LED lines of a scanned wall, beside the photo they are drawn on
 * (docs/SPRAY_WALL_SCAN_PLAN.md): which LEDs each one lights, where it starts,
 * and the way to take it off.
 *
 * **No hold count and no reordering**, unlike the legacy lines: the count is
 * what the line crosses, and the order is the order the lines were drawn —
 * which is the order the strip runs. The LED start stays, for a strip in more
 * than one piece.
 */
export function BoardLedLineList({ lines, ledHolds, ledStarts, selected, onSelect, onChange }: BoardLedLineListProps) {
  const { t } = useTranslation();

  if (lines.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        {t('gymAdmin.boardDashboard.grid.ledLines.empty')}
      </p>
    );
  }

  const setStart = (index: number, ledStart: number | null) =>
    onChange(lines.map((line, at) => (at === index ? { ...line, led_start: ledStart } : line)));

  return (
    <ul className="space-y-1" data-testid="board-led-line-list">
      {lines.map((line, index) => {
        const count = ledHolds[index]?.length ?? 0;
        const first = ledStarts[index] ?? 0;
        return (
          <li
            key={index}
            className={`rounded-md border p-2 ${index === selected ? 'border-primary bg-primary/5' : ''}`}
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="min-w-0 flex-1 text-left text-xs"
                onClick={() => onSelect(index === selected ? null : index)}
                aria-pressed={index === selected}
              >
                <span className="font-medium">
                  {t('gymAdmin.boardDashboard.grid.ledLines.line', { number: index + 1 })}
                </span>{' '}
                <span className="text-muted-foreground">
                  {count > 0
                    ? t('gymAdmin.boardDashboard.grid.ledLines.leds', { count, first, last: first + count - 1 })
                    : t('gymAdmin.boardDashboard.grid.ledLines.noLeds')}
                </span>
              </button>

              <label className="sr-only" htmlFor={`led-line-start-${index}`}>
                {t('gymAdmin.boardDashboard.grid.ledLines.ledStart')}
              </label>
              <Input
                id={`led-line-start-${index}`}
                type="number"
                min={0}
                inputMode="numeric"
                className="h-8 w-16"
                // Empty is the line carrying on from the one before it, which
                // is what the placeholder shows it would be.
                placeholder={String(first)}
                value={line.led_start ?? ''}
                onFocus={() => onSelect(index)}
                onChange={(event) =>
                  setStart(index, event.target.value === '' ? null : Math.max(0, Number(event.target.value) || 0))
                }
              />

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-destructive"
                aria-label={t('gymAdmin.boardDashboard.grid.ledLines.remove', { number: index + 1 })}
                onClick={() => {
                  onChange(lines.filter((_, at) => at !== index));
                  onSelect(null);
                }}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
